import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  RAUTE_SEITE,
  TERMIN_ZEILE,
  ZOOM_SCHRITT,
  balkenBreite,
  gitterBauen,
  istInSpalte,
  istUeberfaellig,
  kopfGruppen,
  lesbar,
  lesbarKurz,
  passendeBeschriftung,
  radZoom,
  tagText,
  termineAnordnen,
  termineHoehe,
  xAm,
  xAmTag,
  zeitraum,
  zoomStufe,
  type Ansicht,
  type Gitter,
} from "./kalender";
import {
  DRUCK_FEST_PX,
  DRUCK_STANDARD,
  DRUCK_GRUPPE_H,
  DRUCK_RAND_MM,
  DRUCK_SPALTE_PX,
  PAPIER,
  aufgabeSichtbar,
  datenBereich,
  druckAuswahl,
  druckBreitePx,
  druckFensterPx,
  druckGitter,
  druckHoehePx,
  druckLegende,
  druckMasse,
  druckZeitraum,
  type Druckfilter,
  type Druckoptionen,
  type Legendeneintrag,
  type Druckmasse,
  type Papier,
} from "./druck";
import { SCHRIFT_KOPF, textBreite } from "./messen";
import { mitGruppenfarbe } from "./stil";
import { PRIO_TEXT, PRIO_ZEICHEN, STATUS_TEXT } from "./texte";
import { Detail } from "./Detail";
import { naechsteTermine, type Status } from "./planner";
import type { Aufgabe, Gruppe, Zeitplan } from "./planner";
import type { Board } from "./laden";
import "./zeitleiste.css";

// Die Zeitleiste. Gerechnet wird in kalender.ts und druck.ts (dort stehen die
// Tests), hier wird nur gezeichnet.

const ANSICHTEN: { wert: Ansicht; name: string }[] = [
  { wert: "woche", name: "Woche" },
  { wert: "monat", name: "Monat" },
  { wert: "quartal", name: "Quartal" },
];

/** Breite, ab der ein Balken seinen Titel trägt – darunter wäre er nur ein Fragment. */
const TITEL_AB = 60;

/** Seitenlänge der Raute im Druck – die Zeilen sind dort halb so hoch. */
const RAUTE_SEITE_DRUCK = 8;

const kopfMessen = (text: string) => textBreite(text, SCHRIFT_KOPF);

const uhrzeit = (d: Date) => d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
const monatJahr = (d: Date) => d.toLocaleDateString("de-DE", { month: "short", year: "numeric" });

type Props = {
  board: Board;
  zeitplan: Zeitplan;
  stand: Date;
  aktualisierungsFehler?: string;
  /** Jetzt aus Planner neu laden (Knopf im Kopf); kommt aus App.tsx. */
  onAktualisieren: () => void;
  /** Läuft gerade eine Aktualisierung? Dann dreht das Symbol und der Knopf ruht. */
  aktualisiertGerade: boolean;
  /** Die kleine Fußzeile mit Anmeldung und Protokoll – kommt aus App.tsx. */
  fusszeile: ReactNode;
};

type Hinweis = { aufgabe: Aufgabe; gruppe: Gruppe; x: number; y: number };

/** Reihenfolge der Status-Chips. Beschriftet mit dem Wert selbst – klein, wie die Zähler im Kopf. */
const STATUS_REIHE: readonly Status[] = ["offen", "aktiv", "erledigt"];

type Druckauftrag = { von: string; bis: string; papier: Papier; optionen: Druckoptionen };

export function Zeitleiste({
  board,
  zeitplan,
  stand,
  aktualisierungsFehler,
  onAktualisieren,
  aktualisiertGerade,
  fusszeile,
}: Props) {
  const [ansicht, setAnsicht] = useState<Ansicht>("monat");
  const [zoom, setZoom] = useState(1);
  const [zugeklappt, setZugeklappt] = useState<ReadonlySet<string>>(new Set());
  const [ausgeblendet, setAusgeblendet] = useState<ReadonlySet<string>>(new Set());
  const [statusAus, setStatusAus] = useState<ReadonlySet<Status>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<Hinweis | null>(null);
  const [druckdialog, setDruckdialog] = useState<Druckauftrag | null>(null);
  const [druckauftrag, setDruckauftrag] = useState<Druckauftrag | null>(null);
  const rollenRef = useRef<HTMLDivElement>(null);

  // Der heutige Tag wird mit jeder Aktualisierung neu bestimmt – ein Tab, der
  // über Mitternacht offen bleibt, zeigt die Heute-Linie sonst am Vortag.
  const heute = useMemo(() => {
    const d = new Date(stand);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [stand]);

  const gruppeVon = useMemo(() => new Map(zeitplan.gruppen.map((g) => [g.id, g])), [zeitplan]);

  // Der Zeitraum richtet sich nach ALLEN Aufgaben, nicht nach den gefilterten:
  // Sonst springt die Zeitleiste bei jedem Filterklick in eine andere Breite.
  const gitter = useMemo(() => {
    const { min, max } = zeitraum(zeitplan.aufgaben, heute);
    return gitterBauen(ansicht, zoom, min, max);
  }, [zeitplan, heute, ansicht, zoom]);

  const zuHeute = useCallback(() => {
    const el = rollenRef.current;
    if (!el) return;
    const spalteW = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--spalte-w"), 10) || 220;
    el.scrollLeft = Math.max(0, xAm(gitter, heute) - (el.clientWidth - spalteW) / 2);
  }, [gitter, heute]);

  // Beim Öffnen und bei jedem Ansichtswechsel auf heute springen. Die Funktion
  // steht über einen Ref bereit, damit der Effekt NUR an der Ansicht hängt –
  // sonst spränge die Ansicht auch bei jedem Zoomschritt zurück.
  const zuHeuteRef = useRef(zuHeute);
  zuHeuteRef.current = zuHeute;
  useLayoutEffect(() => {
    zuHeuteRef.current();
  }, [ansicht]);

  // Escape schließt das Detailfenster.
  useEffect(() => {
    if (!gewaehlt) return;
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") setGewaehlt(null);
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  }, [gewaehlt]);

  // Zoomen mit Strg/Cmd + Mausrad (auch Zusammenziehen am Trackpad) und mit
  // Strg/Cmd + Plus/Minus – wie in der alten App. Ohne `preventDefault`
  // zoomte der Browser bzw. Teams die ganze Seite mit. Das Rad hängt an der
  // Bühne, nicht am Dokument: Über dem Kopf oder einem Dialog bleibt Strg + Rad
  // das, was es sonst ist.
  useEffect(() => {
    const el = rollenRef.current;
    if (!el) return;
    let rest = 0;
    const beiRad = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const r = radZoom(rest, e.deltaY);
      rest = r.rest;
      if (r.richtung !== 0) setZoom((z) => zoomStufe(z + r.richtung * ZOOM_SCHRITT));
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const richtung = e.key === "+" || e.key === "=" ? 1 : e.key === "-" ? -1 : 0;
      if (richtung === 0) return;
      e.preventDefault();
      setZoom((z) => zoomStufe(z + richtung * ZOOM_SCHRITT));
    };
    el.addEventListener("wheel", beiRad, { passive: false });
    document.addEventListener("keydown", beiTaste);
    return () => {
      el.removeEventListener("wheel", beiRad);
      document.removeEventListener("keydown", beiTaste);
    };
  }, []);

  const filter: Druckfilter = useMemo(
    () => ({ zugeklappt, ausgeblendet, statusAus }),
    [zugeklappt, ausgeblendet, statusAus],
  );

  // ── Drucken ───────────────────────────────────────────────────────────────
  // Der Druckblock wird nur gerendert, solange ein Auftrag läuft. Das
  // Papierformat kommt als @page-Regel in den Kopf – es hängt von der Wahl im
  // Dialog ab und kann deshalb nicht im Stylesheet stehen. Danach wird alles
  // wieder aufgeräumt, egal wie der Druckdialog verlassen wurde.
  useEffect(() => {
    if (!druckauftrag) return;
    const p = PAPIER[druckauftrag.papier];
    const regel = document.createElement("style");
    regel.textContent = `@page { size: ${p.breiteMm}mm ${p.hoeheMm}mm; margin: ${DRUCK_RAND_MM}mm; }`;
    document.head.appendChild(regel);

    // Aufgeräumt wird ERST auf afterprint – nicht, wenn print() zurückkehrt.
    // In manchen Browsern kehrt print() zurück, bevor die Druckvorlage gebaut
    // ist; wäre der Block dann schon weg, käme ein leeres Blatt heraus.
    const fertig = () => setDruckauftrag(null);
    window.addEventListener("afterprint", fertig, { once: true });
    // Erst zeichnen lassen, dann drucken: window.print() nimmt den Stand, der
    // im Moment des Aufrufs im Dokument steht.
    const zeit = setTimeout(() => window.print(), 80);
    return () => {
      clearTimeout(zeit);
      window.removeEventListener("afterprint", fertig);
      regel.remove();
    };
  }, [druckauftrag]);

  /** Alles, was ein Druckauftrag braucht – dieselbe Rechnung für Vorschau im Dialog und Blatt. */
  const druckVorbereiten = useCallback(
    (auftrag: Druckauftrag) => {
      const { min, max } = druckZeitraum(auftrag.von, auftrag.bis);
      const auswahl = druckAuswahl(zeitplan, min, max, filter);
      const masse = druckMasse(auswahl.zeilen, auswahl.gruppen.length, auftrag.papier);
      const legende = druckLegende(auswahl, auftrag.optionen, tagText(heute));
      return { min, max, auswahl, masse, legende, gitter: druckGitter(min, max, druckFensterPx(auftrag.papier)) };
    },
    [zeitplan, filter, heute],
  );

  const druck = useMemo(() => (druckauftrag ? druckVorbereiten(druckauftrag) : null), [druckauftrag, druckVorbereiten]);

  const druckdialogOeffnen = () => {
    const { von, bis } = datenBereich(zeitplan.aufgaben, heute);
    setDruckdialog({ von, bis, papier: "a3", optionen: DRUCK_STANDARD });
  };

  const umschalten = <T,>(menge: ReadonlySet<T>, id: T): ReadonlySet<T> => {
    const neu = new Set(menge);
    if (!neu.delete(id)) neu.add(id);
    return neu;
  };

  const zoomSetzen = (z: number) => setZoom(zoomStufe(z));

  const zaehler = { offen: 0, aktiv: 0, erledigt: 0 };
  for (const a of zeitplan.aufgaben) zaehler[a.status]++;

  const zeitraumText = (a: Aufgabe) =>
    a.start === a.ende ? lesbar(a.start) : `${lesbar(a.start)} – ${lesbar(a.ende)}`;

  const gewaehlteAufgabe = gewaehlt ? zeitplan.aufgaben.find((a) => a.id === gewaehlt) : undefined;
  const gewaehlteGruppe = gewaehlteAufgabe ? gruppeVon.get(gewaehlteAufgabe.gruppeId) : undefined;

  return (
    <div className="zeit-app">
      <SchmalHinweis board={board} zeitplan={zeitplan} heuteText={tagText(heute)} />

      <header className="kopf">
        <h1>{board.title}</h1>
        <div className="stand">
          <span>
            <b>{zeitplan.aufgaben.length}</b> Aufgaben
          </span>
          {/* Die Zähler je Status stehen an den Status-Chips der Filterleiste –
              nicht zusätzlich hier. Dieselbe Zahl an zwei Stellen. */}
          <span className="punkt lang">aktualisiert {uhrzeit(stand)}</span>
        </div>
        {/* Außerhalb von `.stand`: Die Zeile verschwindet auf flachem Schirm,
            der Knopf soll bleiben. */}
        <button
          type="button"
          className={`knopf aktualisieren${aktualisiertGerade ? " dreht" : ""}`}
          aria-label="Jetzt aktualisieren"
          title={`Jetzt aus Planner neu laden (zuletzt ${uhrzeit(stand)})`}
          disabled={aktualisiertGerade}
          onClick={onAktualisieren}
        >
          <span aria-hidden="true">↻</span>
        </button>
        <div className="rechts">
          <div className="segment" role="group" aria-label="Ansicht">
            {ANSICHTEN.map((s) => (
              <button key={s.wert} type="button" aria-pressed={ansicht === s.wert} onClick={() => setAnsicht(s.wert)}>
                {s.name}
              </button>
            ))}
          </div>
          <div className="zoom" title="Auch mit Strg/Cmd + Mausrad oder Strg/Cmd + Plus/Minus">
            <button type="button" className="knopf" aria-label="Kleiner" onClick={() => zoomSetzen(zoom - ZOOM_SCHRITT)}>
              −
            </button>
            <span>{Math.round(zoom * 100)} %</span>
            <button type="button" className="knopf" aria-label="Größer" onClick={() => zoomSetzen(zoom + ZOOM_SCHRITT)}>
              +
            </button>
          </div>
          <button type="button" className="knopf" onClick={zuHeute}>
            Heute
          </button>
          <button type="button" className="knopf" onClick={druckdialogOeffnen}>
            Drucken / PDF
          </button>
        </div>
      </header>

      {aktualisierungsFehler && (
        <p className="warnung" style={{ margin: "8px 16px" }}>
          Aktualisierung fehlgeschlagen – gezeigt wird der Stand von {uhrzeit(stand)}. ({aktualisierungsFehler})
        </p>
      )}

      <div className="filter">
        <span className="filter-titel">Gruppen</span>
        <span className="gruppen-wahl">
          <button type="button" className="wahl-link" onClick={() => setAusgeblendet(new Set())}>
            alle
          </button>
          <button
            type="button"
            className="wahl-link"
            onClick={() => setAusgeblendet(new Set(zeitplan.gruppen.map((g) => g.id)))}
          >
            keine
          </button>
        </span>
        {zeitplan.gruppen.map((g) => (
          <button
            key={g.id}
            type="button"
            className={`chip${g.istMeilenstein ? " termine" : ""}`}
            style={mitGruppenfarbe(g.farbe)}
            aria-pressed={!ausgeblendet.has(g.id)}
            onClick={() => setAusgeblendet((alt) => umschalten(alt, g.id))}
          >
            <i />
            {g.name} <span className="n">{zeitplan.aufgaben.filter((a) => a.gruppeId === g.id).length}</span>
          </button>
        ))}
        <span className="trenner" />
        {/* Ersetzt den früheren Schalter „Erledigte zeigen“: Beides zugleich
            hätte sich widersprechen können (die alte App hatte genau das).
            Ohne eigene Überschrift – die Chips nennen ihren Status selbst.
            Unter etwa 1300 px Breite bricht die Leiste in eine zweite Zeile
            um; so entschieden (05.10.2026), statt die Überschrift „Gruppen“
            auch noch zu streichen. */}
        {STATUS_REIHE.map((st) => (
          <button
            key={st}
            type="button"
            className="chip status-chip"
            aria-pressed={!statusAus.has(st)}
            onClick={() => setStatusAus((alt) => umschalten(alt, st))}
          >
            <span className={`lg lg-${st}`} />
            {st} <span className="n">{zaehler[st]}</span>
          </button>
        ))}
      </div>

      <div className="buehne">
        <div className="rollen" ref={rollenRef}>
          <Raster
            zeitplan={zeitplan}
            gitter={gitter}
            heute={heute}
            ansicht={ansicht}
            filter={filter}
            gewaehlt={gewaehlt}
            onWaehlen={setGewaehlt}
            onHinweis={setHinweis}
            onGruppeUmschalten={(id) => setZugeklappt((alt) => umschalten(alt, id))}
            zeitraumText={zeitraumText}
          />
        </div>
        {gewaehlteAufgabe && gewaehlteGruppe && (
          <Detail
            aufgabe={gewaehlteAufgabe}
            gruppe={gewaehlteGruppe}
            heuteText={tagText(heute)}
            onSchliessen={() => setGewaehlt(null)}
          />
        )}
      </div>

      <Legende uebersprungen={zeitplan.uebersprungen}>{fusszeile}</Legende>

      {hinweis && <Tip hinweis={hinweis} text={zeitraumText(hinweis.aufgabe)} />}

      {druckdialog && (
        <Druckdialog
          werte={druckdialog}
          vorschau={(w) => {
            const v = druckVorbereiten(w);
            return { masse: v.masse, zeilen: v.auswahl.zeilen + v.auswahl.gruppen.length };
          }}
          onAendern={setDruckdialog}
          onAbbrechen={() => setDruckdialog(null)}
          onDrucken={() => {
            setDruckauftrag(druckdialog);
            setDruckdialog(null);
          }}
        />
      )}

      {druckauftrag && druck && (
        <div className="druck" style={druckStil(druckauftrag.papier, druck.masse)}>
          <header className="druck-kopf">
            <h1>{board.title}</h1>
            <span className="unter">
              Jahresplan {monatJahr(druck.min)} – {monatJahr(new Date(druck.max.getFullYear(), druck.max.getMonth() - 1, 1))} ·
              Stand {stand.toLocaleDateString("de-DE")}
            </span>
            <Legende eintraege={druck.legende} />
          </header>
          <Raster
            zeitplan={{ ...zeitplan, gruppen: druck.auswahl.gruppen, aufgaben: druck.auswahl.aufgaben }}
            gitter={druck.gitter}
            heute={heute}
            ansicht="monat"
            filter={filter}
            gewaehlt={null}
            zeitraumText={zeitraumText}
            druck={druckauftrag.optionen}
          />
          <footer className="druck-fuss">
            <span>
              Termine mit Datum, Aufgaben als Balken · Quelle: Microsoft Planner
              {uebersprungenText(zeitplan.uebersprungen)}
              {!druck.masse.passt && <span className="warnung"> · Achtung: passt nicht vollständig auf das Blatt</span>}
            </span>
            <span>zeitplan.ac-zeltlager.de</span>
          </footer>
        </div>
      )}
    </div>
  );
}

/**
 * Was nicht auf der Zeitleiste steht – gleicher Wortlaut auf dem Schirm und auf
 * Papier. Genannt wird nur, was tatsächlich vorkommt: „0 ohne Gruppe“ ist keine
 * Auskunft, sondern Rauschen, und auf dem Blatt ist die Zeile knapp.
 */
function uebersprungenText(u: Zeitplan["uebersprungen"]): string {
  const teile = [
    [u.backlog, "im Backlog"],
    [u.ohneDatum, "ohne Datum"],
    [u.ohneGruppe, "ohne Gruppe"],
  ] as const;
  const genannt = teile.filter(([n]) => n > 0).map(([n, was]) => `${n} ${was}`);
  return genannt.length ? ` · nicht gezeigt: ${genannt.join(", ")}` : "";
}

/** Wie viele Termine der Hinweis im Hochformat zeigt. Drei passen ohne Scrollen. */
const TERMINE_IM_HINWEIS = 3;

/**
 * Was auf einem schmalen Schirm statt der Zeitleiste steht.
 *
 * Eine Gantt-Ansicht lebt davon, dass Balken NEBENEINANDER stehen. Am Handy
 * hochkant sind 2,2 von 24 Monaten sichtbar – da gibt es nichts zu vergleichen.
 * Deshalb hier kein geschrumpftes Abbild, sondern die ehrliche Ansage.
 *
 * Eine eigene Listenansicht wurde am 22.09.2026 bewusst VERWORFEN: Genau die
 * hat Planner schon, dort sogar mit Bearbeiten. Sie nachzubauen hieße, dieselben
 * Daten zweimal zu pflegen – und jede Änderung träfe ab dann zwei Ansichten.
 * Was bleibt, sind die nächsten Termine: die eine Frage, die man unterwegs hat.
 *
 * Ein- und ausgeblendet wird per CSS (`@media`), nicht über den Zustand: So
 * springt beim Laden nichts, und das Drehen wirkt sofort.
 */
function SchmalHinweis({ board, zeitplan, heuteText }: { board: Board; zeitplan: Zeitplan; heuteText: string }) {
  const termine = naechsteTermine(zeitplan, heuteText, TERMINE_IM_HINWEIS);
  return (
    <div className="schmal">
      <h1>{board.title}</h1>
      <p className="drehen">
        <span aria-hidden="true">⟳</span> Dreh dein Gerät quer
      </p>
      <p className="warum">
        Die Zeitleiste zeigt ein ganzes Jahr nebeneinander – dafür braucht sie die Breite.
      </p>
      <p className="warum zu-schmal">
        Die Zeitleiste braucht einen breiteren Bildschirm. Am Rechner oder Tablet öffnen.
      </p>

      {termine.length > 0 && (
        <section className="naechste">
          <h2>Als Nächstes</h2>
          <ul>
            {termine.map((t) => (
              <li key={t.id}>
                {/* Hier volles Datum, nicht die Kurzform: Auf dem Blatt ist die
                    Spalte eng, hier ist Platz – und man liest es im Vorbeigehen. */}
                <span className="wann">
                  {t.start === t.ende ? lesbar(t.start) : `${lesbar(t.start)} – ${lesbar(t.ende)}`}
                </span>
                <span className="was">{t.titel}</span>
              </li>
            ))}
          </ul>
          <p className="woanders">Alle Aufgaben stehen in Planner.</p>
        </section>
      )}
    </div>
  );
}

/** Maße des Druckblocks als CSS-Variablen – die Zahlen kommen aus druck.ts. */
function druckStil(papier: Papier, masse: Druckmasse): CSSProperties {
  return {
    "--spalte-w": `${DRUCK_SPALTE_PX}px`,
    "--zeile-h": `${masse.zeile}px`,
    "--gruppe-h": `${DRUCK_GRUPPE_H}px`,
    "--druck-schrift": `${masse.schrift}px`,
    "--druck-kopf-h": `${DRUCK_FEST_PX.kopf}px`,
    "--druck-tabellenkopf-h": `${DRUCK_FEST_PX.tabellenkopf}px`,
    "--druck-fuss-h": `${DRUCK_FEST_PX.fuss}px`,
    width: druckBreitePx(papier),
    height: druckHoehePx(papier),
  } as CSSProperties;
}

// ── Das Raster: Kopfzeile und alle Zeilen ─────────────────────────────────────
// EIN Raster für Bildschirm und Papier. Der Druck reicht ein anderes Gitter
// herein und schaltet `druck` ein: Termine als Zeilen mit Datum, Balken ohne
// Text, Quartalslinien, kleinere Rauten.

type RasterProps = {
  zeitplan: Zeitplan;
  gitter: Gitter;
  heute: Date;
  ansicht: Ansicht;
  filter: Druckfilter;
  gewaehlt: string | null;
  zeitraumText: (a: Aufgabe) => string;
  /** Gesetzt = Druckfassung. Die Optionen bestimmen, was gezeichnet wird. */
  druck?: Druckoptionen;
  onWaehlen?: (id: string) => void;
  onHinweis?: (h: Hinweis | null) => void;
  onGruppeUmschalten?: (id: string) => void;
};

function Raster({
  zeitplan,
  gitter,
  heute,
  ansicht,
  filter,
  gewaehlt,
  zeitraumText,
  druck,
  onWaehlen,
  onHinweis,
  onGruppeUmschalten,
}: RasterProps) {
  const heuteText = tagText(heute);
  const rauteSeite = druck ? RAUTE_SEITE_DRUCK : RAUTE_SEITE;

  // Was auf dem Blatt gezeigt wird, entscheiden die Optionen aus dem Dialog.
  // Am Bildschirm (druck ist dann nicht gesetzt) gilt immer alles.
  const zeigeHeute = !druck || druck.heuteLinie;
  const zeigeUeberfaellig = !druck || druck.ueberfaellig;
  const zeigePrioritaet = !druck || druck.prioritaeten;
  /**
   * Status FÜR DIE DARSTELLUNG. Ist „Erledigtes kennzeichnen“ abgeschaltet,
   * sieht Erledigtes aus wie alles andere – an EINER Stelle entschieden, damit
   * Beschriftung, Balken und Raute nicht auseinanderlaufen.
   */
  const status = (a: Aufgabe): Aufgabe["status"] =>
    druck && !druck.erledigtKennzeichnen && a.status === "erledigt" ? "offen" : a.status;
  // Auf Papier gibt es kein „heute“: Das Blatt wird einmal im Jahr gedruckt und
  // hängt dann monatelang – eine Linie darauf wäre nach einer Woche eine
  // Falschaussage. Sie wird deshalb gar nicht erst gezeichnet, nicht nur
  // ausgeblendet, und auch die Spalte des laufenden Monats bleibt unmarkiert.
  const heuteLinie = zeigeHeute ? <div className="heute" style={{ left: xAm(gitter, heute) }} /> : null;

  const sichtbar = (a: Aufgabe) => aufgabeSichtbar(a, filter);

  const spaltenBreite = gitter.spalten[0].breite;
  // Im Druck zusätzlich kräftigere Linien an den Quartalsgrenzen. Das Gitter
  // beginnt nicht zwingend an einem Quartal – der Versatz rückt die Linien
  // auf die richtigen Monate.
  const quartalVersatz = (3 - (gitter.anfang.getMonth() % 3)) % 3;
  const spurStil: CSSProperties = druck
    ? {
        width: gitter.gesamtBreite,
        backgroundImage: `linear-gradient(to right, #b8b8b8 1px, transparent 1px), linear-gradient(to right, var(--linie2) 1px, transparent 1px)`,
        backgroundSize: `${3 * spaltenBreite}px 100%, ${spaltenBreite}px 100%`,
        backgroundPosition: `${quartalVersatz * spaltenBreite}px 0, 0 0`,
      }
    : { width: gitter.gesamtBreite, backgroundSize: `${spaltenBreite}px 100%` };

  /** Ein Balken, eine Raute, ein Termin – überall dieselbe Bedienung. */
  const eintrag = (a: Aufgabe, g: Gruppe, klassen: string, stil: CSSProperties, inhalt: ReactNode = null) => (
    <button
      key={a.id}
      type="button"
      className={klassen}
      style={stil}
      aria-label={`${a.titel}, ${g.name}, ${zeitraumText(a)}, ${STATUS_TEXT[a.status]}`}
      onClick={onWaehlen ? () => onWaehlen(a.id) : undefined}
      onMouseEnter={onHinweis ? (e) => onHinweis({ aufgabe: a, gruppe: g, x: e.clientX, y: e.clientY }) : undefined}
      onMouseMove={onHinweis ? (e) => onHinweis({ aufgabe: a, gruppe: g, x: e.clientX, y: e.clientY }) : undefined}
      onMouseLeave={onHinweis ? () => onHinweis(null) : undefined}
    >
      {inhalt}
    </button>
  );

  const raute = (a: Aufgabe, g: Gruppe, top: number | undefined, gewaehltJetzt: string) =>
    eintrag(
      a,
      g,
      `raute ${status(a)}${gewaehltJetzt}`,
      mitGruppenfarbe(g.farbe, { left: xAmTag(gitter, a.start), top, width: rauteSeite, height: rauteSeite }),
    );

  /** Eine Aufgabenzeile: links Beschriftung, rechts Raute oder Balken. Im Druck auch für Termine. */
  const aufgabenzeile = (a: Aufgabe, g: Gruppe, mitDatum: boolean) => {
    const zeichen = zeigePrioritaet ? PRIO_ZEICHEN[a.prioritaet] : "";
    const einzeltermin = a.start === a.ende;
    const breite = balkenBreite(gitter, a.start, a.ende);
    const gewaehltJetzt = gewaehlt === a.id ? " gewaehlt" : "";
    const ueber = zeigeUeberfaellig && istUeberfaellig(a, heuteText) ? " ueberfaellig" : "";
    return (
      <Fragment key={a.id}>
        <div className={`lbl aufgabe ${status(a)}`}>
          {mitDatum && (
            <span className="datum">
              {lesbarKurz(a.start)}
              {einzeltermin ? "" : ` – ${lesbarKurz(a.ende)}`}
            </span>
          )}
          <span className="t">{a.titel}</span>
          {zeichen ? (
            <span className={`prio${a.prioritaet === "dringend" ? " dringend" : ""}`} title={PRIO_TEXT[a.prioritaet]}>
              {zeichen}
            </span>
          ) : null}
        </div>
        <div className="spur aufgabe" style={spurStil}>
          {heuteLinie}
          {einzeltermin
            ? raute(a, g, druck ? undefined : 12, gewaehltJetzt)
            : eintrag(
                a,
                g,
                `balken ${status(a)}${ueber}${gewaehltJetzt}`,
                mitGruppenfarbe(g.farbe, { left: xAmTag(gitter, a.start), width: breite }),
                // Im Druck steht der Titel links in der Liste, der Balken bleibt leer.
                !druck && breite > TITEL_AB ? (
                  <>
                    {/* Priorität direkt am Balken (Alwins Wahl, 22.09.2026). Nur wo
                        der Balken auch seinen Titel trägt – kürzere Balken und die
                        Termine-Zeile bleiben frei, dort steht sie im Detailfenster. */}
                    {zeichen ? (
                      <span className={`prio-zeichen ${a.prioritaet}`} title={PRIO_TEXT[a.prioritaet]}>
                        {zeichen}
                      </span>
                    ) : null}
                    <span className="txt">{a.titel}</span>
                  </>
                ) : null,
              )}
        </div>
      </Fragment>
    );
  };

  const zeilen: ReactNode[] = [];

  for (const g of zeitplan.gruppen) {
    if (filter.ausgeblendet.has(g.id)) continue;
    const liste = zeitplan.aufgaben.filter((a) => a.gruppeId === g.id && sichtbar(a));

    if (g.istMeilenstein && !druck) {
      // Termine teilen sich eine Zeile; was sich überschneidet, rutscht eine
      // Spur tiefer – samt Beschriftung (siehe termineAnordnen).
      const { plaetze, spuren } = termineAnordnen(liste, gitter, textBreite);
      const hoehe = termineHoehe(spuren);
      const nach = new Map(liste.map((a) => [a.id, a]));

      zeilen.push(
        <Fragment key={g.id}>
          <div className="lbl gruppe termine" style={mitGruppenfarbe(g.farbe, { height: hoehe })}>
            <i className="rautenform" />
            {g.name}
            <span className="n">{liste.length}</span>
          </div>
          <div className="spur termine" style={mitGruppenfarbe(g.farbe, { ...spurStil, height: hoehe })}>
            {heuteLinie}
            {plaetze.map((p) => {
              const a = nach.get(p.id);
              if (!a) return null;
              const oben = 5 + p.spur * TERMIN_ZEILE;
              const gewaehltJetzt = gewaehlt === a.id ? " gewaehlt" : "";
              return (
                <Fragment key={a.id}>
                  {p.punkt
                    ? raute(a, g, oben + 2, gewaehltJetzt)
                    : eintrag(
                        a,
                        g,
                        `termin-balken ${status(a)}${gewaehltJetzt}`,
                        mitGruppenfarbe(g.farbe, { left: p.links, width: p.breite, top: oben }),
                        p.innen ? a.titel : null,
                      )}
                  {!p.innen && p.textLinks !== null && (
                    <div className={`termin-txt ${status(a)}`} style={{ left: p.textLinks, top: oben }}>
                      {a.titel}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </Fragment>,
      );
      continue;
    }

    // Im Druck werden die Termine wie eine gewöhnliche Gruppe gezeichnet – jede
    // in ihrer Zeile, mit Datum vor dem Titel. Auf Papier gibt es kein Detail-
    // fenster; das Datum muss auf dem Blatt stehen.
    const istZu = !g.istMeilenstein && filter.zugeklappt.has(g.id);
    zeilen.push(
      <Fragment key={g.id}>
        <button
          type="button"
          className={`lbl gruppe${istZu ? " zu" : ""}`}
          style={mitGruppenfarbe(g.farbe)}
          aria-expanded={!istZu}
          onClick={onGruppeUmschalten && !g.istMeilenstein ? () => onGruppeUmschalten(g.id) : undefined}
        >
          <span className="pfeil" aria-hidden="true">
            ▼
          </span>
          <i className={g.istMeilenstein ? "rautenform" : ""} />
          {g.name}
          <span className="n">{liste.length}</span>
        </button>
        <div className="spur gruppe" style={spurStil}>
          {heuteLinie}
          {/* Eingeklappt: die Aufgaben bleiben als dünne Striche sichtbar. */}
          {istZu &&
            liste.map((a) =>
              eintrag(
                a,
                g,
                `balken ${status(a)}`,
                mitGruppenfarbe(g.farbe, {
                  left: xAmTag(gitter, a.start),
                  width: balkenBreite(gitter, a.start, a.ende),
                  top: 10,
                  height: 10,
                  padding: 0,
                  borderRadius: 2,
                }),
              ),
            )}
        </div>
      </Fragment>,
    );

    if (istZu) continue;
    // Im Druck tragen die Termine ihr Datum in der Liste – dort gibt es kein
    // Detailfenster zum Nachsehen.
    for (const a of liste) zeilen.push(aufgabenzeile(a, g, Boolean(druck) && g.istMeilenstein));
  }

  const kopf = kopfGruppen(gitter.spalten, heute);

  return (
    <div className="gantt">
      <div className="hdr links">Aufgabe</div>
      <div className="hdr">
        <div className="hdr-oben">
          {kopf.map((k, i) => (
            <div key={i} className={zeigeHeute && k.istHeute ? "heute-sp" : ""} style={{ width: k.breite }}>
              {/* eigenes span: es klebt beim Scrollen am linken Rand (zeitleiste.css) */}
              <span>{passendeBeschriftung(k.varianten, k.breite, kopfMessen)}</span>
            </div>
          ))}
        </div>
        <div className="hdr-unten">
          {gitter.spalten.map((c, i) => (
            <div key={i} className={zeigeHeute && istInSpalte(c, heute) ? "heute-sp" : ""} style={{ width: c.breite }}>
              {spaltenText(ansicht, c.text, c.breite)}
            </div>
          ))}
        </div>
      </div>
      {zeilen}
    </div>
  );
}

/**
 * Untere Kopfzeile. Schmale Wochenspalten lassen „KW“ weg, sonst wird die Zahl
 * abgeschnitten; ganz schmale bleiben leer.
 */
function spaltenText(ansicht: Ansicht, text: string, breite: number): string {
  if (breite < 18) return "";
  if (ansicht !== "woche") return text;
  return breite < 40 ? text : `KW ${text}`;
}

// ── Legende ───────────────────────────────────────────────────────────────────

/** Die Muster der Druck-Legende. Welche davon erscheinen, sagt druckLegende(). */
const LEGENDE_MUSTER: Record<Legendeneintrag, ReactNode> = {
  zeitraum: (
    <>
      <i className="lg lg-voll" /> Zeitraum
    </>
  ),
  einzeltermin: (
    <>
      <i className="lg lg-raute" /> Einzeltermin
    </>
  ),
  erledigt: (
    <>
      <i className="lg lg-erledigt" /> Erledigt
    </>
  ),
  ueberfaellig: (
    <>
      <i className="lg lg-ueberfaellig" /> Fälligkeit überschritten
    </>
  ),
  prioritaet: (
    <>
      <b className="prio-zeichen dringend">{PRIO_ZEICHEN.dringend}</b> Dringend
      <b className="prio-zeichen wichtig">{PRIO_ZEICHEN.wichtig}</b> Wichtig
      <b className="prio-zeichen niedrig">{PRIO_ZEICHEN.niedrig}</b> Niedrig
    </>
  ),
  heute: (
    <>
      <i className="lg lg-heute" /> Stand beim Druck
    </>
  ),
};

type LegendeProps =
  /** Druck: genau die Einträge, die auf diesem Blatt vorkommen. */
  | { eintraege: Legendeneintrag[]; uebersprungen?: never; children?: never }
  /** Bildschirm: alles, dazu der Hinweis auf Übersprungenes und die Fußzeile. */
  | { eintraege?: never; uebersprungen: Zeitplan["uebersprungen"]; children?: ReactNode };

function Legende(props: LegendeProps) {
  // Auf Papier wird nur genannt, was auch darauf zu sehen ist – die Liste
  // kommt aus druckLegende() und wird nicht von Hand gepflegt. Vorher stand
  // sie fest im Code und behauptete Dinge, die gar nicht gedruckt wurden.
  if (props.eintraege) {
    return (
      <div className="legende">
        {props.eintraege.map((e) => (
          <span key={e} className={e === "prioritaet" ? "lg-prio" : undefined}>
            {LEGENDE_MUSTER[e]}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="legende">
      <span>
        <i className="lg lg-offen" /> Offen
      </span>
      <span>
        <i className="lg lg-aktiv" /> Aktiv
      </span>
      <span>
        <i className="lg lg-erledigt" /> Erledigt
      </span>
      <span>
        <i className="lg lg-raute" /> Einzeltermin
      </span>
      <span>
        <i className="lg lg-heute" /> Heute
      </span>
      <span className="lg-prio">{LEGENDE_MUSTER.prioritaet}</span>
      <span className="rechts">
        Fälligkeit überschritten: roter Rand rechts
        {uebersprungenText(props.uebersprungen)}
      </span>
      {props.children}
    </div>
  );
}

// ── Druckdialog ───────────────────────────────────────────────────────────────

/** Die Schalter im Druckdialog. Reihenfolge = Reihenfolge im Dialog. */
const DRUCK_SCHALTER: { feld: keyof Druckoptionen; text: string }[] = [
  { feld: "erledigtKennzeichnen", text: "Erledigtes kennzeichnen (blass und durchgestrichen)" },
  { feld: "prioritaeten", text: "Prioritäten (‼ ! ↓)" },
  { feld: "ueberfaellig", text: "Fälligkeit überschritten (roter Rand)" },
  { feld: "heuteLinie", text: "Linie am heutigen Tag" },
];

type DruckdialogProps = {
  werte: Druckauftrag;
  /** Rechnet mit denselben Funktionen wie der Druck selbst, damit die Vorschau nicht lügt. */
  vorschau: (w: Druckauftrag) => { masse: Druckmasse; zeilen: number };
  onAendern: (w: Druckauftrag) => void;
  onAbbrechen: () => void;
  onDrucken: () => void;
};

function Druckdialog({ werte, vorschau, onAendern, onAbbrechen, onDrucken }: DruckdialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && !el.open) el.showModal();
  }, []);

  const zeitraumOk = werte.von <= werte.bis;
  const v = useMemo(() => (zeitraumOk ? vorschau(werte) : null), [werte, zeitraumOk, vorschau]);
  const setze = <K extends keyof Druckauftrag>(k: K, wert: Druckauftrag[K]) => onAendern({ ...werte, [k]: wert });

  return (
    <dialog className="druckdialog" ref={ref} onClose={onAbbrechen} aria-labelledby="druck-titel">
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (zeitraumOk) onDrucken();
        }}
      >
        <h2 id="druck-titel">Jahresplan drucken oder als PDF sichern</h2>
        <div className="felder">
          <label>
            Von
            <input type="date" value={werte.von} onChange={(e) => setze("von", e.target.value)} required />
          </label>
          <label>
            Bis
            <input type="date" value={werte.bis} onChange={(e) => setze("bis", e.target.value)} required />
          </label>
          <label className="breit">
            Papier
            <select value={werte.papier} onChange={(e) => setze("papier", e.target.value as Papier)}>
              {(Object.keys(PAPIER) as Papier[]).map((p) => (
                <option key={p} value={p}>
                  {PAPIER[p].name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <fieldset className="optionen">
          <legend>Auf dem Blatt zeigen</legend>
          {DRUCK_SCHALTER.map(({ feld, text }) => (
            <label key={feld} className="schalter">
              <input
                type="checkbox"
                checked={werte.optionen[feld]}
                onChange={(e) => onAendern({ ...werte, optionen: { ...werte.optionen, [feld]: e.target.checked } })}
              />
              {text}
            </label>
          ))}
        </fieldset>
        {!zeitraumOk && <p className="fehler">„Bis“ liegt vor „Von“.</p>}
        {v && (
          <p className="hinweis">
            Ein Blatt {PAPIER[werte.papier].name}, {v.zeilen} Zeilen, Schrift ≈ {Math.round(v.masse.schrift * 0.75 * 10) / 10} pt.
            Gedruckt wird, was gerade sichtbar ist – abgewählte Gruppen und Status bleiben weg. Im Druckdialog lässt
            sich statt eines Druckers „Als PDF sichern“ wählen. Dort unter „Weitere Einstellungen“ die Kopf- und Fußzeilen
            abschalten, sonst druckt der Browser Adresse und Datum mit aufs Blatt.
          </p>
        )}
        {v && !v.masse.passt && (
          <p className="fehler">
            Das passt nicht lesbar auf ein Blatt {PAPIER[werte.papier].name}. Zeitraum kürzen, Gruppen ausblenden oder
            A3 wählen.
          </p>
        )}
        <div className="knopfzeile">
          <button type="button" className="knopf" onClick={onAbbrechen}>
            Abbrechen
          </button>
          <button type="submit" className="knopf betont" disabled={!zeitraumOk}>
            Drucken
          </button>
        </div>
      </form>
    </dialog>
  );
}

// ── Kurzinfo am Zeiger ────────────────────────────────────────────────────────

/**
 * Die Lage wird nach dem Zeichnen korrigiert, damit das Fähnchen nicht aus dem
 * Fenster läuft – die eigene Größe kennt man erst dann.
 */
function Tip({ hinweis, text }: { hinweis: Hinweis; text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [lage, setLage] = useState({ left: hinweis.x + 14, top: hinweis.y + 14 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let links = hinweis.x + 14;
    let oben = hinweis.y + 14;
    if (links + r.width > window.innerWidth - 8) links = hinweis.x - r.width - 14;
    if (oben + r.height > window.innerHeight - 8) oben = hinweis.y - r.height - 14;
    setLage({ left: links, top: oben });
  }, [hinweis]);

  return (
    <div className="tip" ref={ref} style={lage} role="presentation">
      <b>{hinweis.aufgabe.titel}</b>
      <span>
        {hinweis.gruppe.name} · {text}
      </span>
    </div>
  );
}
